import React, { useState, useEffect, useCallback, useRef, useContext } from "react";
import Select from "react-select";
import { LoadingPortContext, resolvePageAccess } from "../../context/LoadingPortContext";

import { AgGridReact } from "@ag-grid-community/react";
import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import { RowGroupingModule } from "@ag-grid-enterprise/row-grouping";
import { MenuModule } from "@ag-grid-enterprise/menu";
import { Edit, Trash } from "lucide-react"
import redirectURL from "../redirectURL.js";
import SweetAlert from 'react-bootstrap-sweetalert';
import XLSX from "xlsx/dist/xlsx.full.min.js";
import { getCellValue, findByFieldIgnoreCase, isSameIgnoreCase, getUserIdentityFields } from "../common/utils";

// Hub Operator Master - cloned from hubmaster.js/hubconsigneecitymapping.js
// ("Hub City Freights"): port-scoped exactly like those screens -
// primary_wh_code/primary_warehouse_name come from the navbar's globally
// selected port (locked, not user-pickable here), Hub Location is a dropdown
// sourced live from hub_master for that port (not free text), and Covered
// Cities is a multi-select sourced live from the hub/consignee-city mapping,
// filtered to the chosen hub.
const HubOperatorMaster = () => {

    const gridRef = useRef();
    const portContext = useContext(LoadingPortContext);
    const lastPortRef = useRef(portContext.loading_port_code);

    const modules = [
        ClientSideRowModelModule,
        RowGroupingModule,
        MenuModule
    ];

    // ---------------- SLIDER STATE ----------------
    const [showSlider, setShowSlider] = useState(false);

    var allowActionsRef = useRef(false);
    var allowDeleteRef = useRef(false);

    // ---------------- FORM ----------------
    const [formData, setFormData] = useState({
        primary_wh_code: null,
        primary_warehouse_name: "",
        hub_location: "",
        covered_cities: "",
        hub_operator_code: "",
        hub_operator_name: ""
    });

    const [rowData, setRowData] = useState([]);
    const [editIndex, setEditIndex] = useState(null);
    const [originalFormData, setOriginalFormData] = useState(null);
    const [loadingPortData, setLoadingPortData] = useState([])
    const [hubData, setHubData] = useState([]);
    const [consigneeCityData, setConsigneeCityData] = useState([]);
    const [showBulkSlider, setShowBulkSlider] = useState(false);
    const [bulkFile, setBulkFile] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // ---------------- VALIDATION ----------------
    const showValidationAlert = (message) => {
        setShow(true);
        setBasicType("warning");
        setBasicTitle(message);
    };

    const validateForm = () => {

        if (!formData.primary_wh_code) {
            showValidationAlert("Loading Plant not resolved");
            return false;
        }

        if (!formData.hub_location) {
            showValidationAlert("Select Hub Location");
            return false;
        }

        if (!String(formData.covered_cities || "").trim()) {
            showValidationAlert("Select at least one Covered City");
            return false;
        }

        if (!formData.hub_operator_code.trim()) {
            showValidationAlert("Enter Hub Operator Code");
            return false;
        }

        if (!formData.hub_operator_name.trim()) {
            showValidationAlert("Enter Hub Operator Name");
            return false;
        }

        return true;
    };

    // ---------------- ADD / UPDATE ----------------
    const handleAdd = async () => {
        if (!validateForm()) return;

        const isEdit = editIndex !== null;

        // Adding (not editing): the Hub Location dropdown lists every hub from
        // Hub Master, including ones that already have an operator assigned
        // here - without this check, picking one would silently overwrite the
        // existing row via the upsert instead of warning first.
        if (!isEdit) {
            var alreadyAssigned = (rowData || []).some((each) =>
                String(each.hub_location || "").trim().toUpperCase() === String(formData.hub_location || "").trim().toUpperCase()
            );
            if (alreadyAssigned) {
                showValidationAlert(`Hub Location "${formData.hub_location}" already has an operator assigned. Edit the existing record instead.`);
                return;
            }
        }

        if (isEdit && originalFormData &&
            String(formData.covered_cities || "") === String(originalFormData.covered_cities || "") &&
            String(formData.hub_operator_code || "").trim().toUpperCase() === String(originalFormData.hub_operator_code || "").trim().toUpperCase() &&
            String(formData.hub_operator_name || "").trim().toUpperCase() === String(originalFormData.hub_operator_name || "").trim().toUpperCase()) {
            setShow(true)
            setBasicType("warning")
            setBasicTitle("No Changes Made.")
            closeSlider();
            return;
        }

        setLoadshow("show-m")
        setOverly("show-m")
        const payload = {
            primary_wh_code: formData.primary_wh_code,
            primary_warehouse_name: formData.primary_warehouse_name,
            hub_location: formData.hub_location,
            covered_cities: formData.covered_cities,
            hub_operator_code: String(formData.hub_operator_code || "").trim().toUpperCase(),
            hub_operator_name: String(formData.hub_operator_name || "").trim().toUpperCase(),
            is_active: 1,
            ...getUserIdentityFields()
        };
        await updateHubOperatorData([payload], isEdit ? "Updated Succesfully" : "Added Succesfully")
        await getHubOperatorData(loadingPortCode);

        closeSlider();
        setLoadshow("show-n")
        setOverly("show-n")
    };

    const closeSlider = () => {
        setShowSlider(false);
        setEditIndex(null);
        setFormData({
            primary_wh_code: loadingPortCode,
            primary_warehouse_name: (loadingPortData.find((each) => each.value == loadingPortCode) || {}).label || "",
            hub_location: "",
            covered_cities: "",
            hub_operator_code: "",
            hub_operator_name: ""
        });
        setOriginalFormData(null);
        setShowBulkSlider(false)
        setValidData([])
        setErrors([])
        setFileName("")
    };

    // ---------------- EDIT ----------------
    const handleEdit = (params) => {
        setFormData(params.data);
        setOriginalFormData(params.data);

        setEditIndex(params.rowIndex);
        setShowSlider(true);
    };

    // ---------------- DELETE ----------------

    const handleDelete = async (params) => {

        const payload = {
            primary_wh_code: params.data.primary_wh_code,
            hub_location: params.data.hub_location,
            is_active: 1,
            ...getUserIdentityFields()
        };
        const confirmDelete = window.confirm(
            "Are you sure to delete this record?"
        );

        if (!confirmDelete) return;
        setLoadshow("show-m")
        setOverly("show-m")
        await deleteHubOperatorData([payload])
        await getHubOperatorData(params.data.primary_wh_code);
        setLoadshow("show-n")
        setOverly("show-n")
    };

    const ActionRenderer = (props) => (
        <div className="d-flex justify-content-center align-items-center" style={{ gap: '6px', justifyContent: "center", width: "100%", height: "35px" }}>

            {allowActionsRef.current && <Edit className="text-primary cursor-pointer ml-2"
                size={15} style={{ cursor: "pointer" }} onClick={() => { handleEdit(props) }} />}
            {allowDeleteRef.current && <Trash className="text-danger cursor-pointer ml-2"
                size={15} style={{ cursor: "pointer" }} onClick={() => { handleDelete(props) }} />}
        </div>
    );
    // ---------------- GRID ----------------
    const components = {
        actionRenderer: ActionRenderer
    };
    const columnDefs = [
        {
            headerName: "",
            width: 100,
            cellRenderer: "actionRenderer",
            pinned: "left",
            headerCheckboxSelection: allowDeleteRef.current ? true : false,
            headerCheckboxSelectionFilteredOnly: true,
            checkboxSelection: allowDeleteRef.current ? true : false,
            hide: (!allowActionsRef.current && !allowDeleteRef.current)
        },
        { headerName: "Hub Location", field: "hub_location", width: 190 },
        { headerName: "Covered Cities", field: "covered_cities", width: 320 },
        { headerName: "Hub Operator Code", field: "hub_operator_code", width: 190 },
        { headerName: "Hub Operator Name", field: "hub_operator_name", width: 240 }
    ];

    useEffect(() => {
        const fetchData = async () => {
            setLoadshow("show-m")
            setOverly("show-m")
            var port = await pageAccessFn();
            await getNecessaryData(port);
            setLoadshow("show-n")
            setOverly("show-n")
        }
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getNecessaryData = async (port) => {
        await getHubData({ is_active: 1, loading_port_code: port })
        await getConsigneeCityData({ loading_port_code: port, is_active: 1 })
        await getHubOperatorData(port);
    }

    const getHubData = async (params) => {
        try {
            const response = await redirectURL.post("/masters/getHubMasterData", params);
            if (response.data.status == "success") {
                setHubData(
                    (response.data.hubData || []).map((each) => ({
                        ...each,
                        label: each.hub_name,
                        value: each.hub_name
                    }))
                )
            }
        } catch (error) {
            console.log(error)
        }
    }
    const getConsigneeCityData = async (params) => {
        try {
            const response = await redirectURL.post("/masters/getHubConsigneeCity", params);
            if (response.data.status == "success") {
                setConsigneeCityData(
                    (response.data.hubMappingData || []).map((each) => ({
                        ...each,
                        label: each.consignee_city,
                        value: each.consignee_city
                    }))
                )
            }
        } catch (error) {
            console.log(error)
        }
    }
    const getHubOperatorData = async (port) => {
        try {
            var params = {
                primary_wh_code: port || loadingPortCode,
                is_active: 1
            }
            const response = await redirectURL.post("/masters/getHubOperatorMasterData", params);
            if (response.data.status == "success") {
                setRowData(response.data.hubOperatorData || [])
            }
        } catch (error) {
            console.log(error)
        }
    }
    const updateHubOperatorData = async (params, successTitle = "Updated Succesfully") => {
        try {
            const response = await redirectURL.post("/masters/updateHubOperatorData", params);
            if (response.data.status == "success") {
                setShow(true)
                setBasicType("success")
                setBasicTitle(successTitle)
            }
        } catch (error) {
            console.log(error)
        }
    }
    const deleteHubOperatorData = async (payload) => {
        try {
            const response = await redirectURL.post("/masters/deleteHubOperatorData", payload);
            if (response.data.status == "success") {
                setShow(true)
                setBasicType("success")
                setBasicTitle("Deleted Succesfully")
            }
        } catch (error) {
            console.log(error)
        }
    }
    const handleBulkUpload = async () => {

        try {
            setLoadshow("show-m")
            setOverly("show-m")
            const finalData = validData.map((each) => {
                return (
                    {
                        ...each,
                        primary_wh_code: loadingPortCode,
                        primary_warehouse_name: (loadingPortData.find((each) => each.value == loadingPortCode) || {}).label || "",
                        is_active: 1,
                        ...getUserIdentityFields()
                    }
                )
            })

            await updateHubOperatorData(finalData, "Added Succesfully")
            await getHubOperatorData(loadingPortCode)
            setLoadshow("show-n")
            setOverly("show-n")
            setValidData([])
            setErrors([])
            setFileName("");
            setShowBulkSlider(false);
            setBulkFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }

        } catch (err) {
            console.error(err);
        }
    };

    const handleDeleteSelected = async () => {
        const confirmDelete = window.confirm(
            "Are you sure to delete this record?"
        );

        if (!confirmDelete) return;
        setLoadshow("show-m")
        setOverly("show-m")
        var payload = selectedRows.map((each) => {
            return {
                primary_wh_code: each.primary_wh_code,
                hub_location: each.hub_location,
                is_active: 1,
                ...getUserIdentityFields()
            }
        })
        await deleteHubOperatorData(payload)
        await getHubOperatorData(loadingPortCode)
        setSelectedRows([])
        setLoadshow("show-n")
        setOverly("show-n")
    }
    const [selectedRows, setSelectedRows] = useState([]);

    const [loadshow, setLoadshow] = useState("show-n")
    const [overly, setOverly] = useState("show-n")
    const [pageAccess, setPageAccess] = useState(0);
    const [loadingPortCode, setLoadingPortCode] = useState("");
    const [compCode, setCompCode] = useState("");
    const [errors, setErrors] = useState([]);
    const [validData, setValidData] = useState([]);
    const [fileName, setFileName] = useState("");

    const fileInputRef = useRef(null);

    const onClickHideAll = () => {
        setValidData([])
        setErrors([])
        setFileName("")
    }

    const pageAccessFn = useCallback(async () => {

        setLoadshow("show-m");
        setOverly("show-m");

        const currentUrl = window.location.pathname;

        try {
            const resolved = await resolvePageAccess(currentUrl, portContext);
            if (!resolved) {
                return;
            }
            lastPortRef.current = resolved.loading_port_code;
            setPageAccess(resolved.pageAccess);
            setLoadingPortCode(resolved.loading_port_code);
            setCompCode(resolved.comp_code);
            setLoadingPortData(resolved.lpNames);
            return resolved.loading_port_code;
        } catch (err) {
            console.log(err);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [portContext]);

    // React to the loading port changing from the header's global selector.
    useEffect(() => {
        if (portContext.loading_port_code !== lastPortRef.current) {
            lastPortRef.current = portContext.loading_port_code;
            onGlobalPortChange(portContext.loading_port_code);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [portContext.loading_port_code]);

    const onGlobalPortChange = async (selectedPlant) => {
        if (!selectedPlant || !loadingPortData.some((each) => each.value == selectedPlant)) {
            return;
        }
        setLoadshow("show-m");
        setOverly("show-m");
        setLoadingPortCode(selectedPlant);
        await getNecessaryData(selectedPlant);
        setValidData([]);
        setErrors([]);
        setShowBulkSlider(false);
        setBulkFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
        setLoadshow("show-n");
        setOverly("show-n");
    };

    const customStyles = {
        container: (provided) => ({
            ...provided,
            width: "100%"
        })
    }

    // ---- Covered Cities multi-select helpers ----
    // Stored/sent as a single comma-joined string (matches the backend field
    // shape), but edited as a multi-select - these two just translate between
    // the two representations.
    const coveredCitiesToOptions = (coveredCitiesStr) => {
        const names = String(coveredCitiesStr || "").split(",").map((c) => c.trim()).filter((c) => c);
        return names.map((name) => {
            const match = consigneeCityData.find((each) => isSameIgnoreCase(each.consignee_city, name));
            return match || { label: name, value: name };
        });
    };
    const optionsToCoveredCities = (options) => {
        return (options || []).map((o) => o.value).join(", ");
    };

    const hubFilteredCities = formData.hub_location
        ? consigneeCityData.filter((each) => isSameIgnoreCase(each.hub_name, formData.hub_location))
        : consigneeCityData;

    const handleFileChange = (e) => {

        const file = e.target.files[0];
        e.target.value = ""; // reset immediately

        if (!file) return;
        setFileName(file.name);

        const reader = new FileReader();
        reader.readAsArrayBuffer(file);

        reader.onload = (event) => {

            const workbook = XLSX.read(
                new Uint8Array(event.target.result),
                { type: "array" }
            );

            const sheet = workbook.Sheets[workbook.SheetNames[0]];

            const data = XLSX.utils.sheet_to_json(sheet, { defval: "" });

            validateExcelData(data);
        };
    };

    // ================= VALIDATION =================

    const validateExcelData = (data) => {

        let errorList = [];
        let cleanedData = [];
        const seenInFile = new Set();
        const existingHubLocations = new Set(
            (rowData || []).map((each) => String(each.hub_location || "").trim().toUpperCase())
        );

        data.forEach((row, index) => {

            const rowNumber = index + 2; // Excel row (header = 1)

            let hubLocationInput = String(getCellValue(row, "Hub Location") || "").trim();
            let coveredCitiesInput = String(getCellValue(row, "Covered Cities") || "").trim();
            let operatorCodeRaw = getCellValue(row, "Hub Operator Code");
            let operatorCode = String(operatorCodeRaw || "").trim().toUpperCase();
            let operatorNameRaw = getCellValue(row, "Hub Operator Name");
            let operatorName = String(operatorNameRaw || "").trim().toUpperCase();

            const matchedHub = findByFieldIgnoreCase(hubData, "hub_name", hubLocationInput);
            let hubLocation = matchedHub ? matchedHub.hub_name : hubLocationInput.toUpperCase();

            const hubCityRecords = (consigneeCityData || []).filter((each) => isSameIgnoreCase(each.hub_name, hubLocation));

            let rowErrors = [];

            // ---- Hub Location ----
            if (!hubLocationInput) {
                rowErrors.push("Hub Location is empty");
            } else if (!matchedHub) {
                rowErrors.push(`Hub Location "${hubLocationInput}" does not exist in Hub Master for this plant.`);
            } else if (existingHubLocations.has(hubLocation)) {
                rowErrors.push(`Hub Location "${hubLocationInput}" already has an operator assigned - this row was skipped.`);
            } else if (seenInFile.has(hubLocation)) {
                rowErrors.push(`Duplicate Hub Location "${hubLocationInput}" appears more than once in the uploaded file.`);
            }

            // ---- Covered Cities ----
            const cityNames = coveredCitiesInput.split(",").map((c) => c.trim()).filter((c) => c);
            if (cityNames.length === 0) {
                rowErrors.push("Covered Cities is empty");
            } else if (matchedHub) {
                const badCities = cityNames.filter((c) => !hubCityRecords.some((rec) => isSameIgnoreCase(rec.consignee_city, c)));
                if (badCities.length > 0) {
                    rowErrors.push(`City/Cities not mapped to Hub "${hubLocation}" in Hub City Freights: ${badCities.join(", ")}`);
                }
            }

            // ---- Hub Operator Code / Name ----
            if (!operatorCode) {
                rowErrors.push("Hub Operator Code is empty");
            }
            if (!operatorName) {
                rowErrors.push("Hub Operator Name is empty");
            }

            if (rowErrors.length > 0) {
                errorList.push({
                    row: rowNumber,
                    errors: rowErrors
                });
            } else {
                seenInFile.add(hubLocation);
                cleanedData.push({
                    hub_location: hubLocation,
                    covered_cities: cityNames.map((c) => {
                        const match = hubCityRecords.find((rec) => isSameIgnoreCase(rec.consignee_city, c));
                        return match ? match.consignee_city : c;
                    }).join(", "),
                    hub_operator_code: operatorCode,
                    hub_operator_name: operatorName
                });
            }
        });

        if (cleanedData.length == 0 && errorList.length == 0) {
            errorList.push({
                row: "",
                errors: ["No Data found. all fields are empty"]
            });
        }

        setErrors(errorList);
        setValidData(cleanedData);
    };

    var errTrueUpload = errors.length > 0 && validData.length > 0
    var disableUpload = validData.length == 0
    const getShortFileName = (name, maxLength = 31) => {
        const lastDot = name.lastIndexOf(".");
        const baseName = name.substring(0, lastDot);
        const extension = name.substring(lastDot);

        if (baseName.length <= maxLength) return name;

        return baseName.substring(0, maxLength) + "..." + extension;
    };
    const closeAlert = () => {
        setShow(false)
    }
    const [show, setShow] = useState(false);
    const [basicType, setBasicType] = useState("");
    const [basicTitle, setBasicTitle] = useState("");
    const onExportClick = () => {
        if (gridRef.current && gridRef.current.api) {
            gridRef.current.api.exportDataAsExcel({
                fileName: "hub_operator_master.xlsx"
            });
        }
    };

    if (pageAccess != 2) {
        allowActionsRef.current = true;
    }
    if (pageAccess == 3) {
        allowDeleteRef.current = true;
    }
    return (
        <div className="hub-master-container d-flex flex-column" style={{ height: "100%", overflowY: "auto" }}>
            <SweetAlert
                show={show}
                type={basicType}
                title={basicTitle}
                onConfirm={closeAlert}
                styles={{ zIndex: "10000001" }}
            >
            </SweetAlert>
            {/* HEADER */}
            <div className="master-header">
                <div className="d-flex align-items-center" style={{ gap: "14px" }}>
                    <div style={{ width: "5px", height: "26px", background: "linear-gradient(180deg,#1976d2,#42a5f5)", borderRadius: "3px", flexShrink: 0 }}></div>
                    <h5 style={{ margin: 0, fontSize: "16px", fontWeight: 700, color: "#1a237e", letterSpacing: "0.2px" }}>Hub Operator Master</h5>
                </div>

                <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                    {(selectedRows.length > 0 && allowDeleteRef.current) && <button
                        className="pill-btn pill-btn-danger"
                        onClick={() => { handleDeleteSelected() }}
                        disabled={selectedRows.length == 0}
                    >
                        Delete Selected
                    </button>}
                    <button
                        className="pill-btn pill-btn-primary"
                        onClick={() => { onExportClick() }}
                    >
                        Export Data
                    </button>
                    {allowActionsRef.current && <button
                        className="pill-btn pill-btn-success"
                        onClick={() => {
                            setShowSlider(true);
                            var object = loadingPortData.find((each) => each.value == loadingPortCode)
                            setFormData((prevState) => ({
                                ...prevState,
                                primary_wh_code: object ? object.value : loadingPortCode,
                                primary_warehouse_name: object ? object.label : ""
                            }))
                        }}
                    >
                        + Add
                    </button>}
                    {allowActionsRef.current && <button
                        className="pill-btn pill-btn-info"
                        onClick={() => {
                            setErrors([]);
                            setValidData([]);
                            setFileName("");
                            setShowBulkSlider(true);
                        }}
                    >
                        Import Data
                    </button>}
                </div>
            </div>

            {/* GRID */}
            <div className="ag-theme-balham grid-wrapper mt-2" style={{ flex: 1, minHeight: 0 }}>
                <AgGridReact
                    ref={gridRef}
                    modules={modules}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    defaultColDef={{
                        sortable: true,
                        filter: true,
                        floatingFilter: true, resizable: true
                    }}
                    statusBar={{
                        statusPanels: [
                            { statusPanel: "agSelectedRowCountComponent", align: "left" },
                            { statusPanel: "agFilteredRowCountComponent", align: "center" },
                            { statusPanel: "agTotalRowCountComponent", align: "right" }
                        ]
                    }}
                    sideBar={{
                        toolPanels: [
                            {
                                id: 'columns',
                                labelDefault: 'Columns',
                                iconKey: 'columns',
                                toolPanel: 'agColumnsToolPanel'
                            },
                            {
                                id: 'filters',
                                labelDefault: 'Filters',
                                iconKey: 'filter',
                                toolPanel: 'agFiltersToolPanel'
                            }
                        ],
                    }}
                    animateRows rowSelection={"multiple"}
                    rowHeight={45}
                    headerHeight={45}
                    frameworkComponents={components}
                    onSelectionChanged={() => {
                        if (gridRef && gridRef.current && gridRef.current.api) {
                            const selected = gridRef.current.api.getSelectedRows();
                            setSelectedRows(selected);
                        }
                    }} suppressRowClickSelection={true}
                />
            </div>

            {/* OVERLAY */}
            {showSlider && (
                <div
                    className="slider-overlay"
                    onClick={closeSlider}
                />
            )}
            {showBulkSlider && (
                <div
                    className="slider-overlay"
                    onClick={closeSlider}
                />
            )}

            {/* RIGHT SLIDER */}
            <div className={`hub-slider ${showSlider ? "open" : ""} d-flex flex-column`}>

                <div className="slider-header">
                    {editIndex !== null ? "Edit Hub Operator" : "Add Hub Operator"}
                    <span onClick={closeSlider}>✕</span>
                </div>

                <div className="slider-body flex-grow-1 pt-4">

                    <label className="m-0">Loading Plant<span className="text-danger">*</span></label>
                    <Select
                        options={loadingPortData}
                        value={loadingPortData.find((each) => each.value == formData.primary_wh_code) || null}
                        classNamePrefix="react-select" className="mb-2"
                        isDisabled={true}
                        styles={customStyles}
                    />

                    <label className="m-0">Hub Location<span className="text-danger">*</span></label>
                    <Select
                        options={hubData}
                        value={hubData.find((each) => each.value == formData.hub_location) || null}
                        onChange={(selected) => {
                            setFormData({ ...formData, hub_location: selected.value, covered_cities: "" });
                        }}
                        classNamePrefix="react-select" className="mb-2"
                        isDisabled={editIndex != null}
                        styles={customStyles}
                    />

                    <label className="m-0">Covered Cities<span className="text-danger">*</span></label>
                    <Select
                        options={hubFilteredCities}
                        value={coveredCitiesToOptions(formData.covered_cities)}
                        onChange={(selected) => {
                            setFormData({ ...formData, covered_cities: optionsToCoveredCities(selected) });
                        }}
                        classNamePrefix="react-select" className="mb-2"
                        isMulti
                        isDisabled={!formData.hub_location}
                        styles={customStyles}
                    />

                    <label className="m-0">Hub Operator Code<span className="text-danger">*</span></label>
                    <input
                        name="hub_operator_code"
                        value={formData.hub_operator_code}
                        onChange={handleChange} className="mb-2"
                    />

                    <label className="m-0">Hub Operator Name<span className="text-danger">*</span></label>
                    <input
                        name="hub_operator_name"
                        value={formData.hub_operator_name}
                        onChange={handleChange}
                    />

                </div>
                <div className="slider-footer">
                    <button
                        className="save-btn" style={{ margin: "0px" }}
                        onClick={handleAdd}
                    >
                        {editIndex !== null ? "Update" : "Save"}
                    </button>
                </div>
            </div>

            <div className={`hub-slider ${showBulkSlider ? "open" : ""} d-flex flex-column`}>

                <div className="slider-header">
                    Import Data
                    <span onClick={closeSlider}>✕</span>
                </div>

                <div className="slider-body flex-grow-1">

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={handleFileChange}
                        style={{ display: "none" }}
                    />
                    <div className="d-flex align-items-center gap-3">
                        <button
                            className="btn btn-info d-flex justify-content-center p-3"
                            type="button"
                            onClick={() => fileInputRef.current.click()}
                        >
                            Upload File
                        </button>

                        <a href={require("../../assets/hub_operator_bulk_upload_template.csv")} className="btn btn-secondary d-flex justify-content-center ml-3 p-3 flex-grow-1">
                            Download Sample Template
                        </a>
                    </div>

                    {fileName && (
                        <div style={{ marginTop: "15px", display: "flex", gap: "6px", maxWidth: "410px" }}>
                            <span>Selected File :</span>
                            <span
                                style={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    flex: 1,
                                    fontWeight: "bold"
                                }}
                                title={fileName}
                            >
                                {getShortFileName(fileName)}
                            </span>
                        </div>
                    )}

                    {errors.length > 0 && (
                        <>
                            <h6 className="text-danger mt-3">Validation Errors</h6>

                            <div style={{ flexGrow: 1, overflowY: "auto", width: "100%" }}>
                                <table className="table-bordered mb-0 err-table" style={{ width: "100%", positive: "relative" }}>
                                    <thead className="table-light text-center" style={{ positive: "sticky", zIndex: "100", backgroundColor: "#efefef" }}>
                                        <tr>
                                            <th style={{ width: "60px", padding: "9px 15px" }}>Row</th>
                                            <th>Error Description</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {errors.map((err, i) => (
                                            <tr key={i}>
                                                <td className="text-center" style={{ padding: "9px 15px" }}>
                                                    {err.row}
                                                </td>

                                                <td style={{ padding: "9px 15px", wordBreak: "break-word" }}>
                                                    {err.errors.map((e, idx) => (
                                                        <p className="m-0" key={idx}>{e}</p>
                                                    ))}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                </div>
                <div className="slider-footer">
                    <button
                        className="save-btn" style={{ margin: "0px", width: "auto", padding: "5px 25px", cursor: disableUpload && "not-allowed", opacity: disableUpload && "0.5" }}
                        onClick={handleBulkUpload}
                        disabled={disableUpload}
                    >
                        {errTrueUpload ? "Submit only valid data" : "Submit"}
                    </button>
                </div>

            </div>

            <div className={"dataLoadpageimg " + (loadshow)} style={{ zIndex: 100 }}>
                <div className="loader-box"><div className="loader-box"><div className="rotate dashed colored"></div></div></div>
            </div>
            <div className={"overlay-part " + (overly)} onClick={onClickHideAll}></div>

        </div>
    );
};

export default HubOperatorMaster;
